/*
 * Copyright (C) 2013 Spanning Cloud Apps.  All rights reserved.
 */

'use strict';
angular
  .module('http.queue', [])
  /**
   * Service used to access all calls to the batch rest service.
   */
  .factory('httpQueue', function ($http) {
    /**
     * Function called to submit a backup.
     *
     * @returns {Response|Error} An http response object on success, an Error on failure.
     */
    var submitBackup = function () {
      return $http({
        method: 'POST',
        url: 'api/queue/backup'
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Function called to submit an export.
     *
     * @param {number} batchId - The batchId of the batch to export.
     * @param {string|string[]} types - Either an array of object types to export, or
     *  the string ALL for the entire batch.
     * @param {string[]} metadataTypes - an array of metadata types to export.
     *
     * @returns {Response|Error} An http response object on success, an Error on failure.
     */
    var submitExport = function (batchId, types, metadataTypes) {
      return $http({
        method: 'POST',
        url: 'api/queue/export',
        data: {
          batchIdToExport: batchId,
          types: types,
          metadataTypes: metadataTypes
        }
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Function called to submit an sobject restore.
     *
     * @param {number} recordId - The record id of the item which will be restored.
     * @param {number} recordBatchId - The record batch id of the item which will be restored.
     * @param [String[]) fields - An array of field to be updated.
     * @param [Number[]) attachments - An array of attachment ids to be added to the object.
     *
     * @returns {Response|Error} An http response object on success, an Error on failure.
     */
    const submitSObjectRestore = function (recordId, recordBatchId, fields, attachments) {
      return $http({
        method: 'POST',
        url: 'api/queue/restore/sobject',
        data: {
          recordId,
          recordBatchId,
          fields,
          attachmentIdentities: attachments
        }
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Function called to submit a bulk restore. Either batchId will be defined or startDate and endDate will be defined.
     *
     * @param {Object} params - A data object
     * @param {String} params.sobjectName - The object type.
     * @param {Number[]} params.sobjectStates - The object states.
     * @param {Number} params.totalObjects - The total number of items to restore.
     * @param {Boolean} params.includeChildren -  Flag to include children in restore or not.
     * @param {Boolean} params.csvVerifiedRestore - Flag to use csv verified restore or regular restore
     * @param {Number} params.targetOrgId -  The sf org id of the target org.
     * @param {String} [params.filter] -  The filter string to apply.
     * @param {Number} [params.batchId] - The id of batch of items to restore.
     * @param {Number} [params.start] - The start batch id.
     * @param {Number} [params.end] - The end batch id.
     * @param {Number[]|String} params.items - The items to restore or string 'ALL' for all items.
     *
     * @returns {Response|Error} An http response object on success, an Error on failure.
     */
    var submitBulkRestore = function (params) {
      return $http({
        method: 'POST',
        url: 'api/queue/bulk-restore',
        data: params
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Function called to submit a bulk restore preview. Either batchId will be defined or start and end batch will be defined.
     *
     * @param {Object} params - A data object
     * @param {String} params.sobjectName - The object type.
     * @param {Number[]} params.sobjectStates - The object states.
     * @param {Number} params.totalObjects - The total number of items to restore.
     * @param {Boolean} params.includeChildren -  Flag to include children in restore or not.
     * @param {String} [params.filter] -  The filter string to apply.
     * @param {Number} [params.batchId] - The id of batch of items to restore.
     * @param {Number} [params.start] - The start batch id.
     * @param {Number} [params.end] - The end batch id.
     * @param {Number[]|String} params.items - The items to restore or string 'ALL' for all items.
     *
     * @returns {Response|Error} An http response object on success, an Error on failure.
     */
    var submitBulkRestorePreview = function (params) {
      return $http({
        method: 'POST',
        url: 'api/queue/bulk-restore-preview',
        data: params
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Function called to submit a bulk restore preview of failed items.
     *
     * @param {Object} params - A data object
     * @param {Number} params.batchId - The id of batch of items to restore.
     *
     * @returns {Response|Error} An http response object on success, an Error on failure.
     */
    var submitBulkRestoreFailedRecords = function (params) {
      return $http({
        method: 'POST',
        url: 'api/queue/bulk-restore-failed',
        data: params
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Function called to submit a bulk restore preview of failed items.
     *
     * @param {Object} params - A data object
     * @param {Number} params.batchId - The id of batch of items to restore.
     * @param {Number} params.uploadTimestamp - The timestamp used on the s3 key.
     * @param {String} params.fileName - The name of the file uploaded. This is used for UI purposes when user re-enters restore flow after verification.
     * @param {Number} params.sourceOrgId - The spanning org id of the source org.
     * @param {Number} params.targetOrgId - The spanning org id of the target org.
     * @param {String} params.parentJobType - Parent job type for which we verify CSV
     * @param {Number} [params.accessTokenOwnerId] - The access token owner id of the cross org auth. Needed for a cross org restore.
     *
     * @returns {Response|Error} An http response object on success, an Error on failure.
     */
    var submitBulkRestoreCsvVerification = function (params) {
      return $http({
        method: 'POST',
        url: 'api/queue/csv-verification',
        data: params
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Function called to submit a metadata compare job.
     *
     * @param {Object} params - A data object
     * @param {String} leftSfOrgId - The left Salesforce Org id.
     * @param {String} rightSfOrgId - The right Salesforce Org id.
     * @param {Number} leftBatchId - The left batch id.
     * @param {Number} rightBatchId - The right batch id.
     * @param {String[] | String} components - The components to compare or 'ALL' to compare all
     *
     * @returns {Response|Error} An http response object on success, an Error on failure.
     */
    var submitMetadataCompare = function (params) {
      return $http({
        method: 'POST',
        url: 'api/queue/metadata-compare',
        data: params
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Function called to submit a metadata restore job.
     *
     * @param {Object} params - A parameters object
     * @param {String} params.sourceSfOrgId - The source Salesforce org id.
     * @param {String} params.targetSfOrgId - The target Salesforce org id.
     * @param {Number} params.sourceBatchId - The ID of a backup batch we wish to restore data from.
     * @param {Object} params.metadataObjects - The metadata component types and items beneath them to be restored.
     *
     * @returns {Response|Error} - An http response object on success, an Error on failure.
     */
    var submitMetadataRestore = function (params) {
      return $http({
        method: 'POST',
        url: 'api/queue/metadata-restore',
        data: params
      }).then(function (res) {
        return res.data;
      });
    };

    /**
     * Function called to submit a gdpr request.
     *
     * @param {Object} params - A data object
     * @returns {Response|Error} An http response object on success, an Error on failure.
     */
    const submitGdprOperation = function (params) {
      return $http({
        method: 'POST',
        url: 'api/queue/gdpr-request',
        data: params
      }).then(function (res) {
        return res.data;
      });
    };

    return {
      submitBackup: submitBackup,
      submitExport: submitExport,
      submitSObjectRestore: submitSObjectRestore,
      submitBulkRestore: submitBulkRestore,
      submitBulkRestorePreview: submitBulkRestorePreview,
      submitBulkRestoreFailedRecords: submitBulkRestoreFailedRecords,
      submitBulkRestoreCsvVerification: submitBulkRestoreCsvVerification,
      submitMetadataCompare: submitMetadataCompare,
      submitMetadataRestore: submitMetadataRestore,
      submitGdprOperation
    };
  });
